import { useCallback } from 'react';
import { BlockerFunction, useBlocker } from 'react-router';
import { useModal } from '../Modal/useModal';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import Modal from '../Modal/Modal';
import MiniLoader from '../MiniLoader/MiniLoader';

interface Props {
  blockCondition: boolean;
  saveChanges: () => Promise<void>;
  isLoading: boolean;
}

export const ConfirmLeaveWithoutChanges = ({
  blockCondition,
  saveChanges,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const {
    isModalOpened: isModalOpened,
    closeModal: closeModal,
    openModal: openModal,
  } = useModal();

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      const isBlocked =
        blockCondition && currentLocation.pathname !== nextLocation.pathname;

      if (isBlocked) {
        openModal();
      }

      return isBlocked;
    },
    // eslint-disable-next-line
    [blockCondition]
  );

  const blocker = useBlocker(shouldBlock);

  return (
    <Modal
      isOpened={isModalOpened}
      close={closeModal}
      title={t('Confirm action')}
      isLoading={isLoading}
      name='confirm-modal'
    >
      <div className='mx-4 px-4 pb-6 pt-2'>
        <>
          <div className='Text mb-2 font-black'>
            {t('Leave without saving')}
          </div>
          <div>{t('You have unsaved changes.')}</div>
          <div>
            {t('Are you sure you want to leave the page without saving?')}
          </div>
          <div className='mb-4'>
            <Button
              data-testid='save_changes_modal_btn'
              className='!font-semibold'
              styling='naked-blue'
              onClick={async () => {
                await saveChanges();
                if (blocker.proceed) {
                  closeModal();
                  blocker.proceed();
                }
              }}
            >
              {t('Save changes')}
            </Button>
            &nbsp;
            <span>{t('and leave the page')}.</span>
          </div>
        </>
        <div className='mt-2 flex flex-wrap gap-x-2'>
          <div>
            <Button
              styling='red'
              className='flex items-center'
              type='submit'
              disabled={isLoading}
              data-testid='confirm-btn'
              onClick={() => {
                if (blocker.proceed) {
                  closeModal();
                  blocker.proceed();
                }
              }}
            >
              {isLoading && <MiniLoader className='mr-2' color='gray' />}
              <span>{t('Leave without saving')}</span>
            </Button>
          </div>
          <div>
            <Button
              styling='outline'
              onClick={closeModal}
              disabled={isLoading}
              data-testid='cancel-btn'
            >
              {t('Cancel')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
